import { generateShortUUID } from '@atlaskit/editor-plugins/local-id/utils/generateShortUUID';
import type {
	Fragment,
	Node as PMNode,
	NodeType,
	ResolvedPos,
} from '@atlaskit/editor-prosemirror/model';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { findWrapping } from '@atlaskit/editor-prosemirror/transform';
import type { Transform } from '@atlaskit/editor-prosemirror/transform';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ToolCallArguments } from '../../../aiSuggestionsPluginType';

function wrapChild(
	fragment: Fragment,
	type: NodeType,
	attrs: Record<string, unknown>,
): PMNode | null {
	if (!fragment.firstChild) {
		return null;
	}

	const wrappingNodeTypes = type.contentMatch.findWrapping(fragment.firstChild.type);
	let currentChildNodes: readonly PMNode[] | null = null;
	if (wrappingNodeTypes) {
		currentChildNodes = fragment.content;
		for (let i = wrappingNodeTypes.length - 1; i >= 0; i--) {
			const wrappingNodeType = wrappingNodeTypes[i];
			const nextChildNode = wrappingNodeType.createAndFill(null, currentChildNodes);
			if (nextChildNode) {
				currentChildNodes = [nextChildNode];
			} else {
				currentChildNodes = null;
				break;
			}
		}
	}
	if (currentChildNodes) {
		return type.createAndFill(attrs, currentChildNodes);
	}

	return null;
}

function findPosForNode(node: PMNode, $to: ResolvedPos): number | null {
	for (let depth = $to.depth; depth >= 0; depth--) {
		const ancestor = $to.node(depth);
		if (ancestor.type.contentMatch.matchType(node.type)) {
			return $to.after(depth + 1);
		}
	}
	return null;
}

export const wrapNode = ({
	toolCallArguments,
	tr,
	pos,
}: {
	pos: number;
	toolCallArguments: ToolCallArguments<'wrap' | 'wrapNode'>;
	tr: Transaction | Transform;
}): Transaction | Transform => {
	const $from = tr.doc.resolve(pos);
	const node = tr.doc.nodeAt(pos);
	if (!node) {
		return tr;
	}
	const $to = tr.doc.resolve(pos + node.nodeSize);
	const range = $from.blockRange($to);
	if (range) {
		const type = tr.doc.type.schema.nodes[toolCallArguments.wrapperType];
		try {
			// A wrapper type that supports `localId` gets one up-front, as the block menu's
			// convert-to-expand does. Without it the local-id plugin's `appendTransaction` assigns
			// one via a `BatchAttrsStep` before the view updates, which swaps the wrapper node
			// object out and drops any state keyed on its identity — including the `expandedState`
			// entry `openWrappedExpands` sets to keep a wrapped expand open. `generateShortUUID` is
			// that plugin's own generator, so the id matches the ones it assigns and is registered
			// for its collision checks.
			const needsLocalId =
				fg('platform_editor_ai_show_diff_patch_1') &&
				Boolean(type.spec.attrs?.localId) &&
				typeof toolCallArguments.wrapperAttrs?.localId !== 'string';
			const attrs = needsLocalId
				? { ...toolCallArguments.wrapperAttrs, localId: generateShortUUID() }
				: toolCallArguments.wrapperAttrs;
			const wrapping = findWrapping(range, type, attrs);
			if (wrapping !== null) {
				tr.wrap(range, wrapping);
			} else {
				const slice = tr.doc.slice(range.$from.pos, range.$to.pos);
				const fragment = slice.content;
				let node = type.createAndFill(attrs, fragment);
				if (!node) {
					node = wrapChild(fragment, type, attrs);
				}
				if (node) {
					const insertPos = findPosForNode(node, $to);
					if (insertPos !== null) {
						tr.insert(insertPos, node);
						tr.replaceRange(range.$from.pos, range.$to.pos, Slice.empty);
					}
				}
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
		}
	}
	return tr;
};
